import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  Plus,
  Pencil,
  Trash2,
  Mail,
  CheckCheck,
  Building2,
  MessageSquare,
  BarChart3,
  X
} from "lucide-react";
import { useI18n } from "../context/I18nContext";
import { adminApi, fetchProjects, fetchStats } from "../lib/api";

const CATEGORIES = ["construction", "renovation", "architecture", "consulting"];

const emptyProject = () => ({
  title: { en: "", tr: "", ru: "" },
  description: { en: "", tr: "", ru: "" },
  category: "construction",
  image_url: "",
  year: new Date().getFullYear(),
  location: "",
  featured: false
});

const emptyStat = () => ({
  label: { en: "", tr: "", ru: "" },
  value: 0,
  suffix: "+",
  icon: "Building2",
  order: 0
});

const AdminPage = () => {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [tab, setTab] = useState("projects");
  const [projects, setProjects] = useState([]);
  const [stats, setStats] = useState([]);
  const [messages, setMessages] = useState([]);
  const [editing, setEditing] = useState(null); // {type, data}
  const [loading, setLoading] = useState(false);

  const password = useMemo(() => sessionStorage.getItem("mayak_admin"), []);
  const client = useMemo(() => (password ? adminApi(password) : null), [password]);

  useEffect(() => {
    if (!password) navigate("/");
  }, [password, navigate]);

  const loadAll = useCallback(async () => {
    if (!client) return;
    try {
      const [p, s, m] = await Promise.all([
        fetchProjects(),
        fetchStats(),
        client.get("/messages").then((r) => r.data)
      ]);
      setProjects(p);
      setStats(s);
      setMessages(m);
    } catch (e) {
      if (e?.response?.status === 401) {
        sessionStorage.removeItem("mayak_admin");
        navigate("/");
      }
    }
  }, [client, navigate]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  const logout = () => {
    sessionStorage.removeItem("mayak_admin");
    navigate("/");
  };

  // Project handlers
  const saveProject = async (data) => {
    setLoading(true);
    try {
      if (data.id) {
        await client.put(`/projects/${data.id}`, data);
      } else {
        await client.post("/projects", data);
      }
      setEditing(null);
      await loadAll();
    } finally {
      setLoading(false);
    }
  };
  const deleteProject = async (id) => {
    if (!window.confirm("Delete this project?")) return;
    await client.delete(`/projects/${id}`);
    await loadAll();
  };

  const saveStat = async (data) => {
    setLoading(true);
    try {
      if (data.id) await client.put(`/stats/${data.id}`, data);
      else await client.post("/stats", data);
      setEditing(null);
      await loadAll();
    } finally {
      setLoading(false);
    }
  };
  const deleteStat = async (id) => {
    if (!window.confirm("Delete this stat?")) return;
    await client.delete(`/stats/${id}`);
    await loadAll();
  };

  const markRead = async (id) => {
    await client.put(`/messages/${id}/read`);
    await loadAll();
  };
  const deleteMessage = async (id) => {
    if (!window.confirm("Delete this message?")) return;
    await client.delete(`/messages/${id}`);
    await loadAll();
  };

  return (
    <div className="min-h-screen bg-mayak-bg-alt text-mayak-text" data-testid="admin-page">
      {/* Top bar */}
      <header className="bg-mayak-surface text-mayak-text border-b border-mayak-border">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12 h-20 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate("/")}
              className="text-mayak-dim hover:text-mayak-accent flex items-center gap-2 text-xs uppercase tracking-[0.2em]"
              data-testid="admin-back-button"
            >
              <ArrowLeft size={14} /> Site
            </button>
            <div className="w-px h-6 bg-mayak-border" />
            <span className="font-black text-lg">
              MAYAK<span className="text-mayak-accent">.</span>{" "}
              <span className="text-mayak-faint font-normal text-xs uppercase tracking-[0.3em] ml-2">
                {t("admin.title")}
              </span>
            </span>
          </div>
          <button
            onClick={logout}
            className="ghost-btn !py-2.5 !px-4 !text-[10px]"
            data-testid="admin-logout"
          >
            {t("admin.logout")}
          </button>
        </div>
      </header>

      {/* Tabs */}
      <div className="border-b border-mayak-border bg-mayak-surface">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12 flex gap-2">
          <TabButton
            active={tab === "projects"}
            onClick={() => setTab("projects")}
            icon={Building2}
            label={t("admin.tabs.projects")}
            count={projects.length}
            testId="admin-tab-projects"
          />
          <TabButton
            active={tab === "messages"}
            onClick={() => setTab("messages")}
            icon={MessageSquare}
            label={t("admin.tabs.messages")}
            count={messages.length}
            testId="admin-tab-messages"
            badge={messages.filter((m) => !m.read).length}
          />
          <TabButton
            active={tab === "stats"}
            onClick={() => setTab("stats")}
            icon={BarChart3}
            label={t("admin.tabs.stats")}
            count={stats.length}
            testId="admin-tab-stats"
          />
        </div>
      </div>

      <main className="max-w-[1440px] mx-auto px-6 lg:px-12 py-12">
        {tab === "projects" && (
          <ProjectsPanel
            projects={projects}
            onNew={() => setEditing({ type: "project", data: emptyProject() })}
            onEdit={(p) => setEditing({ type: "project", data: p })}
            onDelete={deleteProject}
            t={t}
          />
        )}
        {tab === "stats" && (
          <StatsPanel
            stats={stats}
            onNew={() => setEditing({ type: "stat", data: emptyStat() })}
            onEdit={(s) => setEditing({ type: "stat", data: s })}
            onDelete={deleteStat}
            t={t}
          />
        )}
        {tab === "messages" && (
          <MessagesPanel
            messages={messages}
            onMarkRead={markRead}
            onDelete={deleteMessage}
            t={t}
          />
        )}
      </main>

      {editing && editing.type === "project" && (
        <ProjectEditor
          initial={editing.data}
          onClose={() => setEditing(null)}
          onSave={saveProject}
          loading={loading}
          t={t}
        />
      )}
      {editing && editing.type === "stat" && (
        <StatEditor
          initial={editing.data}
          onClose={() => setEditing(null)}
          onSave={saveStat}
          loading={loading}
          t={t}
        />
      )}
    </div>
  );
};

const TabButton = ({ active, onClick, icon: Icon, label, count, badge, testId }) => (
  <button
    onClick={onClick}
    data-testid={testId}
    className={`relative flex items-center gap-2 px-5 py-4 border-b-2 transition-all ${
      active
        ? "border-mayak-accent text-mayak-text"
        : "border-transparent text-mayak-dim hover:text-mayak-text"
    }`}
  >
    <Icon size={16} strokeWidth={1.6} />
    <span className="text-sm font-semibold tracking-tight">{label}</span>
    <span className="text-xs text-mayak-faint font-mono">({count})</span>
    {badge > 0 && (
      <span className="ml-1 inline-flex items-center justify-center min-w-5 h-5 px-1.5 bg-mayak-accent text-mayak-bg text-[10px] font-bold">
        {badge}
      </span>
    )}
  </button>
);

const ProjectsPanel = ({ projects, onNew, onEdit, onDelete, t }) => (
  <div data-testid="admin-projects-panel">
    <div className="flex items-center justify-between mb-8">
      <div>
        <h2 className="text-3xl font-black tracking-tight">{t("admin.tabs.projects")}</h2>
        <p className="text-sm text-mayak-dim mt-1">{projects.length} total</p>
      </div>
      <button onClick={onNew} className="magnetic-btn !text-[10px]" data-testid="admin-new-project">
        <Plus size={14} />
        {t("admin.newProject")}
      </button>
    </div>

    <div className="bg-mayak-surface border border-mayak-border overflow-hidden">
      <table className="w-full">
        <thead className="bg-mayak-bg-alt border-b border-mayak-border">
          <tr>
            <th className="text-left px-6 py-4 text-[10px] uppercase tracking-[0.2em] text-mayak-dim">Image</th>
            <th className="text-left px-6 py-4 text-[10px] uppercase tracking-[0.2em] text-mayak-dim">Title (EN)</th>
            <th className="text-left px-6 py-4 text-[10px] uppercase tracking-[0.2em] text-mayak-dim">Category</th>
            <th className="text-left px-6 py-4 text-[10px] uppercase tracking-[0.2em] text-mayak-dim">Year</th>
            <th className="text-left px-6 py-4 text-[10px] uppercase tracking-[0.2em] text-mayak-dim">Location</th>
            <th className="text-right px-6 py-4 text-[10px] uppercase tracking-[0.2em] text-mayak-dim">Actions</th>
          </tr>
        </thead>
        <tbody>
          {projects.map((p) => (
            <tr key={p.id} className="border-b border-mayak-border hover:bg-mayak-bg-alt">
              <td className="px-6 py-4">
                <img src={p.image_url} alt="" className="w-16 h-12 object-cover" />
              </td>
              <td className="px-6 py-4 text-sm font-semibold text-mayak-text">
                {p.title?.en}
                {p.featured && <span className="ml-2 text-[10px] uppercase tracking-[0.2em] text-mayak-accent">Featured</span>}
              </td>
              <td className="px-6 py-4 text-sm text-mayak-text/70 capitalize">{p.category}</td>
              <td className="px-6 py-4 text-sm text-mayak-text/70">{p.year}</td>
              <td className="px-6 py-4 text-sm text-mayak-text/70">{p.location}</td>
              <td className="px-6 py-4">
                <div className="flex justify-end gap-2">
                  <button
                    onClick={() => onEdit(p)}
                    className="p-2 hover:bg-mayak-bg-alt"
                    data-testid={`admin-edit-project-${p.id}`}
                  >
                    <Pencil size={14} />
                  </button>
                  <button
                    onClick={() => onDelete(p.id)}
                    className="p-2 hover:bg-red-50 hover:text-red-600"
                    data-testid={`admin-delete-project-${p.id}`}
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              </td>
            </tr>
          ))}
          {projects.length === 0 && (
            <tr>
              <td colSpan={6} className="px-6 py-12 text-center text-mayak-faint">
                No projects yet.
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  </div>
);

const StatsPanel = ({ stats, onNew, onEdit, onDelete, t }) => (
  <div data-testid="admin-stats-panel">
    <div className="flex items-center justify-between mb-8">
      <div>
        <h2 className="text-3xl font-black tracking-tight">{t("admin.tabs.stats")}</h2>
        <p className="text-sm text-mayak-dim mt-1">{stats.length} total</p>
      </div>
      <button onClick={onNew} className="magnetic-btn !text-[10px]" data-testid="admin-new-stat">
        <Plus size={14} />
        {t("admin.newStat")}
      </button>
    </div>
    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
      {stats.map((s) => (
        <div key={s.id} className="bg-mayak-surface border border-mayak-border p-6 group">
          <div className="text-[10px] uppercase tracking-[0.3em] text-mayak-dim">
            {s.label?.en}
          </div>
          <div className="mt-2 font-black text-4xl text-mayak-text tabular-nums">
            {s.value}
            {s.suffix}
          </div>
          <div className="mt-1 text-xs text-mayak-faint">
            Order {s.order} · {s.icon}
          </div>
          <div className="mt-4 flex gap-2">
            <button
              onClick={() => onEdit(s)}
              className="text-xs flex items-center gap-1 px-3 py-1.5 border border-mayak-border-strong hover:border-mayak-text"
              data-testid={`admin-edit-stat-${s.id}`}
            >
              <Pencil size={12} /> Edit
            </button>
            <button
              onClick={() => onDelete(s.id)}
              className="text-xs flex items-center gap-1 px-3 py-1.5 border border-mayak-border-strong hover:border-red-500 hover:text-red-600"
              data-testid={`admin-delete-stat-${s.id}`}
            >
              <Trash2 size={12} /> Del
            </button>
          </div>
        </div>
      ))}
    </div>
  </div>
);

const MessagesPanel = ({ messages, onMarkRead, onDelete, t }) => (
  <div data-testid="admin-messages-panel">
    <div className="flex items-center justify-between mb-8">
      <div>
        <h2 className="text-3xl font-black tracking-tight">{t("admin.tabs.messages")}</h2>
        <p className="text-sm text-mayak-dim mt-1">
          {messages.length} total · {messages.filter((m) => !m.read).length} unread
        </p>
      </div>
    </div>

    {messages.length === 0 ? (
      <div className="bg-mayak-surface border border-mayak-border p-16 text-center text-mayak-faint">
        {t("admin.noMessages")}
      </div>
    ) : (
      <div className="space-y-3">
        {messages.map((m) => (
          <div
            key={m.id}
            className={`bg-mayak-surface border p-6 ${
              m.read ? "border-mayak-border" : "border-mayak-accent"
            }`}
            data-testid={`admin-message-${m.id}`}
          >
            <div className="flex items-start justify-between gap-6">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-2">
                  <Mail size={14} className="text-mayak-accent" />
                  <span className="font-semibold text-mayak-text">{m.name}</span>
                  <span className="text-mayak-dim text-sm">·</span>
                  <a href={`mailto:${m.email}`} className="text-sm text-mayak-text/70 hover:text-mayak-text">
                    {m.email}
                  </a>
                  {!m.read && (
                    <span className="ml-2 px-2 py-0.5 bg-mayak-accent text-mayak-bg text-[10px] uppercase tracking-[0.2em]">
                      New
                    </span>
                  )}
                </div>
                {m.phone && (
                  <div className="text-xs text-mayak-dim mb-1">📞 {m.phone}</div>
                )}
                {m.subject && (
                  <div className="text-sm font-semibold text-mayak-text mb-2">
                    {m.subject}
                  </div>
                )}
                <p className="text-sm text-mayak-text leading-relaxed whitespace-pre-line">
                  {m.message}
                </p>
                <div className="mt-3 text-[10px] uppercase tracking-[0.3em] text-mayak-faint font-mono">
                  {new Date(m.created_at).toLocaleString()}
                </div>
              </div>
              <div className="flex flex-col gap-2 shrink-0">
                {!m.read && (
                  <button
                    onClick={() => onMarkRead(m.id)}
                    className="text-xs flex items-center gap-1 px-3 py-1.5 border border-mayak-border-strong hover:border-mayak-text"
                    data-testid={`admin-mark-read-${m.id}`}
                  >
                    <CheckCheck size={12} /> Read
                  </button>
                )}
                <button
                  onClick={() => onDelete(m.id)}
                  className="text-xs flex items-center gap-1 px-3 py-1.5 border border-mayak-border-strong hover:border-red-500 hover:text-red-600"
                  data-testid={`admin-delete-message-${m.id}`}
                >
                  <Trash2 size={12} /> Del
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    )}
  </div>
);

const ProjectEditor = ({ initial, onClose, onSave, loading, t }) => {
  const [data, setData] = useState(initial);

  const setField = (k, v) => setData((d) => ({ ...d, [k]: v }));
  const setLocalized = (parent, lang, val) =>
    setData((d) => ({ ...d, [parent]: { ...d[parent], [lang]: val } }));

  return (
    <div className="fixed inset-0 z-[80] bg-black/60 flex items-center justify-center p-4 overflow-y-auto" data-testid="project-editor-modal">
      <div className="bg-mayak-surface max-w-3xl w-full max-h-[90vh] overflow-y-auto border border-mayak-border-strong">
        <div className="flex items-center justify-between p-6 border-b border-mayak-border sticky top-0 bg-mayak-surface z-10">
          <h3 className="font-black text-2xl">{data.id ? t("admin.edit") : t("admin.newProject")}</h3>
          <button onClick={onClose} className="p-2 hover:bg-mayak-bg-alt">
            <X size={18} />
          </button>
        </div>
        <div className="p-6 space-y-6">
          <LangFieldSet label={t("admin.fields.title")} value={data.title} onChange={(l, v) => setLocalized("title", l, v)} />
          <LangFieldSet label={t("admin.fields.description")} value={data.description} textarea onChange={(l, v) => setLocalized("description", l, v)} />

          <div className="grid grid-cols-2 gap-4">
            <Field label={t("admin.fields.category")}>
              <select
                value={data.category}
                onChange={(e) => setField("category", e.target.value)}
                className="w-full border border-mayak-border-strong px-3 py-2.5 bg-mayak-bg text-mayak-text text-sm"
                data-testid="project-edit-category"
              >
                {CATEGORIES.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </Field>
            <Field label={t("admin.fields.year")}>
              <input
                type="number"
                value={data.year}
                onChange={(e) => setField("year", parseInt(e.target.value) || 0)}
                className="w-full border border-mayak-border-strong px-3 py-2.5 text-sm bg-mayak-bg text-mayak-text"
                data-testid="project-edit-year"
              />
            </Field>
          </div>
          <Field label={t("admin.fields.location")}>
            <input
              value={data.location}
              onChange={(e) => setField("location", e.target.value)}
              className="w-full border border-mayak-border-strong px-3 py-2.5 text-sm bg-mayak-bg text-mayak-text"
              data-testid="project-edit-location"
            />
          </Field>
          <Field label={t("admin.fields.image")}>
            <input
              value={data.image_url}
              onChange={(e) => setField("image_url", e.target.value)}
              className="w-full border border-mayak-border-strong px-3 py-2.5 text-sm bg-mayak-bg text-mayak-text"
              data-testid="project-edit-image"
            />
            {data.image_url && (
              <img src={data.image_url} alt="" className="mt-3 w-full max-h-40 object-cover" />
            )}
          </Field>
          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={data.featured}
              onChange={(e) => setField("featured", e.target.checked)}
              data-testid="project-edit-featured"
            />
            <span className="text-sm">{t("admin.fields.featured")}</span>
          </label>
        </div>
        <div className="flex justify-end gap-3 p-6 border-t border-mayak-border sticky bottom-0 bg-mayak-surface">
          <button onClick={onClose} className="ghost-btn !py-3 !text-xs !border-mayak-border !text-mayak-text" data-testid="project-edit-cancel">
            {t("admin.cancelAction")}
          </button>
          <button
            onClick={() => onSave(data)}
            disabled={loading}
            className="magnetic-btn !text-xs"
            data-testid="project-edit-save"
          >
            {loading ? "…" : t("admin.save")}
          </button>
        </div>
      </div>
    </div>
  );
};

const StatEditor = ({ initial, onClose, onSave, loading, t }) => {
  const [data, setData] = useState(initial);
  const setField = (k, v) => setData((d) => ({ ...d, [k]: v }));
  const setLocalized = (parent, lang, val) =>
    setData((d) => ({ ...d, [parent]: { ...d[parent], [lang]: val } }));

  return (
    <div className="fixed inset-0 z-[80] bg-black/60 flex items-center justify-center p-4 overflow-y-auto" data-testid="stat-editor-modal">
      <div className="bg-mayak-surface max-w-2xl w-full max-h-[90vh] overflow-y-auto border border-mayak-border-strong">
        <div className="flex items-center justify-between p-6 border-b border-mayak-border">
          <h3 className="font-black text-2xl">{data.id ? t("admin.edit") : t("admin.newStat")}</h3>
          <button onClick={onClose} className="p-2 hover:bg-mayak-bg-alt">
            <X size={18} />
          </button>
        </div>
        <div className="p-6 space-y-6">
          <LangFieldSet label={t("admin.fields.label")} value={data.label} onChange={(l, v) => setLocalized("label", l, v)} />
          <div className="grid grid-cols-2 gap-4">
            <Field label={t("admin.fields.value")}>
              <input
                type="number"
                value={data.value}
                onChange={(e) => setField("value", parseInt(e.target.value) || 0)}
                className="w-full border border-mayak-border-strong px-3 py-2.5 text-sm bg-mayak-bg text-mayak-text"
                data-testid="stat-edit-value"
              />
            </Field>
            <Field label={t("admin.fields.suffix")}>
              <input
                value={data.suffix || ""}
                onChange={(e) => setField("suffix", e.target.value)}
                className="w-full border border-mayak-border-strong px-3 py-2.5 text-sm bg-mayak-bg text-mayak-text"
                data-testid="stat-edit-suffix"
              />
            </Field>
            <Field label="Icon">
              <select
                value={data.icon || "Building2"}
                onChange={(e) => setField("icon", e.target.value)}
                className="w-full border border-mayak-border-strong px-3 py-2.5 bg-mayak-bg text-mayak-text text-sm"
                data-testid="stat-edit-icon"
              >
                {["Building2", "Award", "Users", "HardHat"].map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </Field>
            <Field label={t("admin.fields.order")}>
              <input
                type="number"
                value={data.order}
                onChange={(e) => setField("order", parseInt(e.target.value) || 0)}
                className="w-full border border-mayak-border-strong px-3 py-2.5 text-sm bg-mayak-bg text-mayak-text"
                data-testid="stat-edit-order"
              />
            </Field>
          </div>
        </div>
        <div className="flex justify-end gap-3 p-6 border-t border-mayak-border">
          <button onClick={onClose} className="ghost-btn !py-3 !text-xs !border-mayak-border !text-mayak-text">
            {t("admin.cancelAction")}
          </button>
          <button onClick={() => onSave(data)} disabled={loading} className="magnetic-btn !text-xs" data-testid="stat-edit-save">
            {loading ? "…" : t("admin.save")}
          </button>
        </div>
      </div>
    </div>
  );
};

const Field = ({ label, children }) => (
  <label className="block">
    <span className="block text-[10px] uppercase tracking-[0.2em] text-mayak-dim mb-2">
      {label}
    </span>
    {children}
  </label>
);

const LangFieldSet = ({ label, value, onChange, textarea }) => (
  <div>
    <span className="block text-[10px] uppercase tracking-[0.2em] text-mayak-dim mb-3">
      {label}
    </span>
    <div className="grid grid-cols-1 gap-2">
      {["en", "tr", "ru"].map((l) => (
        <div key={l} className="flex gap-3 items-start">
          <span className="text-[10px] uppercase tracking-[0.2em] text-mayak-accent font-mono w-8 pt-2.5">{l}</span>
          {textarea ? (
            <textarea
              value={(value && value[l]) || ""}
              onChange={(e) => onChange(l, e.target.value)}
              rows={2}
              className="flex-1 border border-mayak-border-strong px-3 py-2.5 text-sm resize-y bg-mayak-bg text-mayak-text"
              data-testid={`lang-${l}-${label}`}
            />
          ) : (
            <input
              value={(value && value[l]) || ""}
              onChange={(e) => onChange(l, e.target.value)}
              className="flex-1 border border-mayak-border-strong px-3 py-2.5 text-sm bg-mayak-bg text-mayak-text"
              data-testid={`lang-${l}-${label}`}
            />
          )}
        </div>
      ))}
    </div>
  </div>
);

export default AdminPage;
